/**
 * AIMING — where the crosshair is.
 *
 * Three input modes, and they are PEERS, not a primary and two fallbacks
 * (ROUNDTABLE.md, A-2). WCAG 2.5.4 Motion Actuation is a Level A criterion: it
 * requires that functionality operated by device motion also be operable by
 * conventional controls, and that the motion response be disableable. A touch
 * path that engages only when permission is refused satisfies neither half —
 * a player who granted permission and then finds aiming painful would have no
 * way out. So the mode is a setting, switchable mid-wave, and all three modes
 * feed the same stage coordinates into the same shot message.
 *
 *   gyro    point the phone. Relative to a calibration pose.
 *   touch   drag with a thumb. Relative, with gain.
 *   mouse   the cursor IS the sight. Absolute.
 *
 * ── Why mouse aiming is absolute ────────────────────────────────────────────
 * The other two are relative because the input has no natural mapping onto the
 * stage — a thumb is somewhere on the glass and a wrist is at some angle, and
 * neither means a point. A cursor already means a point. Adding a sensitivity
 * gain to it would take the one input in the game that can be exactly where the
 * player is looking and make it not that, which is why `sensitivity` is
 * deliberately ignored in this mode and the settings sheet says so.
 *
 * ── The gyro path, and the trap it avoids ───────────────────────────────────
 * `DeviceOrientationEvent` reports intrinsic Tait-Bryan angles in Z-X'-Y''
 * order (α about z, β about x', γ about y''). That convention has a
 * singularity at β = ±90°, where γ and α become degenerate and the reported
 * values swing wildly for an essentially motionless device.
 *
 * A phone held up in front of you to aim is AT β ≈ 90°. It is not an edge case
 * for this product; it is the default posture.
 *
 * So nothing here subtracts Euler angles. Each reading is converted to a
 * quaternion at the event boundary, the relative rotation against the
 * calibration quaternion is computed with quaternion algebra, and yaw and pitch
 * are read off a rotated forward vector. The Euler triple never appears again
 * after line one of `onOrientation`.
 *
 * ── Steadiness ──────────────────────────────────────────────────────────────
 * Raw orientation at ~60 Hz jitters by around a degree at rest, which at aiming
 * magnification is several pixels of tremor on a crosshair. A fixed low-pass
 * removes it and adds lag you feel when swinging onto a running deer. The One
 * Euro filter (Casiez, Roussel & Vogel, CHI 2012) is an adaptive low-pass whose
 * cutoff rises with speed: still hands get heavy smoothing, fast swings get
 * almost none. That trade is the entire reason it exists, and it is the right
 * one here.
 */

import { pxToStage, type StageFit } from '@shared/stage.js';

export type AimMode = 'gyro' | 'touch' | 'mouse';

export type GyroStatus =
  | 'idle'
  | 'requesting'
  /** Permission granted and readings are arriving. */
  | 'live'
  /** The player said no. */
  | 'denied'
  /** No sensor, no secure context, or nothing ever fired. */
  | 'unavailable';

export interface AimState {
  sx: number;
  sy: number;
  mode: AimMode;
  gyro: GyroStatus;
}

// ── quaternions ─────────────────────────────────────────────────────────────

type Quat = [number, number, number, number]; // x, y, z, w

const DEG = Math.PI / 180;

/**
 * The W3C DeviceOrientation → quaternion conversion, ZXY intrinsic.
 * `alpha` about z, `beta` about x', `gamma` about y''.
 */
function quatFromEuler(alpha: number, beta: number, gamma: number): Quat {
  const z = alpha * DEG;
  const x = beta * DEG;
  const y = gamma * DEG;

  const cX = Math.cos(x / 2);
  const cY = Math.cos(y / 2);
  const cZ = Math.cos(z / 2);
  const sX = Math.sin(x / 2);
  const sY = Math.sin(y / 2);
  const sZ = Math.sin(z / 2);

  return [
    sX * cY * cZ - cX * sY * sZ,
    cX * sY * cZ + sX * cY * sZ,
    cX * cY * sZ + sX * sY * cZ,
    cX * cY * cZ - sX * sY * sZ,
  ];
}

function quatMul(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

function quatConj(q: Quat): Quat {
  return [-q[0], -q[1], -q[2], q[3]];
}

/** Rotation of `angle` radians about the device's z axis. */
function quatZ(angle: number): Quat {
  return [0, 0, Math.sin(angle / 2), Math.cos(angle / 2)];
}

/** Apply a quaternion to a vector. */
function rotate(q: Quat, v: [number, number, number]): [number, number, number] {
  const [qx, qy, qz, qw] = q;
  const [vx, vy, vz] = v;
  // t = 2 * (q_vec × v); result = v + qw*t + q_vec × t
  const tx = 2 * (qy * vz - qz * vy);
  const ty = 2 * (qz * vx - qx * vz);
  const tz = 2 * (qx * vy - qy * vx);
  return [
    vx + qw * tx + (qy * tz - qz * ty),
    vy + qw * ty + (qz * tx - qx * tz),
    vz + qw * tz + (qx * ty - qy * tx),
  ];
}

// ── the One Euro filter ─────────────────────────────────────────────────────

class LowPass {
  private y: number | null = null;
  filter(x: number, alpha: number): number {
    this.y = this.y === null ? x : alpha * x + (1 - alpha) * this.y;
    return this.y;
  }
  reset(to: number | null = null) {
    this.y = to;
  }
}

class OneEuro {
  private x = new LowPass();
  private dx = new LowPass();
  private prev: number | null = null;

  constructor(
    private minCutoff = 1.0,
    private beta = 0.35,
    private dCutoff = 1.0,
  ) {}

  private alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  filter(value: number, dt: number): number {
    if (dt <= 0 || dt > 0.25) dt = 1 / 60;
    const derivative = this.prev === null ? 0 : (value - this.prev) / dt;
    this.prev = value;
    const edx = this.dx.filter(derivative, this.alpha(this.dCutoff, dt));
    const cutoff = this.minCutoff + this.beta * Math.abs(edx);
    return this.x.filter(value, this.alpha(cutoff, dt));
  }

  reset() {
    this.x.reset();
    this.dx.reset();
    this.prev = null;
  }
}

// ── settings ────────────────────────────────────────────────────────────────

/**
 * How many radians of wrist rotation sweep the crosshair from the centre to the
 * edge of the stage. 24° at default sensitivity: enough that the whole stage is
 * reachable without moving your shoulder, tight enough that a deer at the
 * treeline is not a one-pixel wrist twitch.
 */
const SWEEP_RAD = 24 * DEG;

/** The crosshair may leave the stage slightly, so the edges are reachable. */
const LIMIT = 1.12;

export interface AimOptions {
  /** 0.5 … 2.0. Persisted in the player's settings. */
  sensitivity: number;
  /** Gyro only: invert the vertical axis, for players who read it flight-sim. */
  invertY: boolean;
}

export class AimController {
  readonly state: AimState = { sx: 0, sy: 0, mode: 'touch', gyro: 'idle' };

  private opts: AimOptions = { sensitivity: 1, invertY: false };

  private ref: Quat | null = null;
  private yawF = new OneEuro();
  private pitchF = new OneEuro();
  private lastEventAt = 0;
  private sawEvent = false;
  private listening = false;
  private probeTimer: number | null = null;

  /** Touch-mode drag bookkeeping. */
  private dragging = false;
  private dragId = -1;
  private lastTouch: { x: number; y: number } | null = null;

  private onChange: (() => void) | null = null;

  constructor(private readonly onFire: () => void) {}

  subscribe(cb: () => void) {
    this.onChange = cb;
  }

  setOptions(next: Partial<AimOptions>) {
    this.opts = { ...this.opts, ...next };
  }

  getOptions(): AimOptions {
    return { ...this.opts };
  }

  /**
   * Switch input mode. Available at any time from the pause sheet, including
   * mid-wave — that is the half of WCAG 2.5.4 that a permission-triggered
   * fallback misses.
   */
  setMode(mode: AimMode) {
    this.state.mode = mode;
    if (mode !== 'gyro') this.stopGyro();
    if (mode === 'touch') {
      // Handing a thumb-aimer a crosshair parked wherever the gyro left it is
      // a shot they did not choose. Relative modes start from the middle.
      this.state.sx = 0;
      this.state.sy = 0;
    }
    this.notify();
  }

  /**
   * Ask for motion permission. MUST be called from a user gesture handler on
   * iOS — `DeviceOrientationEvent.requestPermission()` rejects otherwise, and
   * the rejection is indistinguishable from the player having said no.
   */
  async requestGyro(): Promise<GyroStatus> {
    if (typeof window === 'undefined' || typeof window.DeviceOrientationEvent === 'undefined') {
      return this.setGyro('unavailable');
    }

    const anyDOE = window.DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };

    if (typeof anyDOE.requestPermission === 'function') {
      this.setGyro('requesting');
      try {
        const res = await anyDOE.requestPermission();
        if (res !== 'granted') return this.setGyro('denied');
      } catch {
        // Thrown when not in a user gesture, or in an insecure context. Both
        // are "you are not getting readings", which is what matters here.
        return this.setGyro('unavailable');
      }
    }

    this.startGyro();

    /**
     * Permission is not the same as readings. Desktop Chrome has the API and
     * fires nothing; a phone with a failed IMU does the same. So probe: if no
     * event has arrived in 800 ms, this device does not have the input, and the
     * player is told that rather than left aiming a crosshair that never moves.
     */
    return await new Promise<GyroStatus>((resolve) => {
      this.probeTimer = window.setTimeout(() => {
        this.probeTimer = null;
        resolve(this.sawEvent ? this.setGyro('live') : this.setGyro('unavailable'));
      }, 800);
    });
  }

  private setGyro(status: GyroStatus): GyroStatus {
    this.state.gyro = status;
    if (status === 'live') this.state.mode = 'gyro';
    // No sensor, or the player said no. Land on whichever conventional input
    // this machine actually has rather than always on the thumb — a desktop
    // player asked for the gyro out of curiosity should get their mouse back,
    // not a drag-to-aim mode built for a phone.
    else if (status === 'denied' || status === 'unavailable') this.state.mode = finePointer() ? 'mouse' : 'touch';
    this.notify();
    return status;
  }

  private startGyro() {
    if (this.listening) return;
    this.listening = true;
    this.sawEvent = false;
    window.addEventListener('deviceorientation', this.onOrientation, true);
  }

  private stopGyro() {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener('deviceorientation', this.onOrientation, true);
    if (this.probeTimer !== null) {
      clearTimeout(this.probeTimer);
      this.probeTimer = null;
    }
  }

  private onOrientation = (e: DeviceOrientationEvent) => {
    if (e.alpha === null || e.beta === null || e.gamma === null) return;
    this.sawEvent = true;
    if (this.state.mode !== 'gyro') return;

    const now = performance.now();
    const dt = this.lastEventAt ? (now - this.lastEventAt) / 1000 : 1 / 60;
    this.lastEventAt = now;

    // Euler → quaternion, once, right here. Nothing downstream ever sees the
    // three angles again — that is the whole point (A-1).
    let q = quatFromEuler(e.alpha, e.beta, e.gamma);

    /**
     * Screen-orientation compensation. `screen.orientation.angle` is how far
     * the SCREEN is rotated relative to the device's natural orientation.
     * Without this, turning the phone to landscape swaps the crosshair's axes
     * and inverts one of them — the classic "my aiming is sideways now" bug.
     */
    const angle = (screen.orientation?.angle ?? 0) * DEG;
    if (angle) q = quatMul(q, quatZ(-angle));

    if (!this.ref) {
      this.ref = q;
      this.yawF.reset();
      this.pitchF.reset();
      return;
    }

    // Relative rotation since calibration. Quaternion algebra, no singularity.
    const rel = quatMul(quatConj(this.ref), q);

    /**
     * The barrel. `-z` in the device frame points out of the BACK of the phone,
     * which is the direction the phone is "pointing" when it is held up with
     * the screen toward you — the laser-pointer mental model the crosshair is
     * built on.
     */
    const [vx, vy, vz] = rotate(rel, [0, 0, -1]);

    const yaw = Math.atan2(vx, -vz);
    const pitch = Math.asin(Math.max(-1, Math.min(1, vy)));

    const sYaw = this.yawF.filter(yaw, dt);
    const sPitch = this.pitchF.filter(pitch, dt);

    const gain = 1 / (SWEEP_RAD / this.opts.sensitivity);
    this.state.sx = clampAim(sYaw * gain);
    this.state.sy = clampAim(-sPitch * gain * (this.opts.invertY ? -1 : 1));
    this.notify();
  };

  /** Take the current pose as dead centre. */
  recenter() {
    this.ref = null;
    this.yawF.reset();
    this.pitchF.reset();
    this.state.sx = 0;
    this.state.sy = 0;
    this.notify();
  }

  // ── pointers: thumb and mouse ────────────────────────────────────────────

  /**
   * Touch aiming is RELATIVE, not absolute. Absolute would put the player's
   * thumb exactly on top of the thing they are trying to shoot, which is the
   * one place it must not be. Dragging moves the crosshair with a gain; a quick
   * tap that did not travel is a shot.
   *
   * Mouse aiming is the opposite on both counts: the sight is already under the
   * cursor, so a press is unambiguously a trigger pull and fires on the way
   * DOWN. Waiting for the release would add the whole click duration to every
   * shot's timestamp, which on a moving deer is a real miss.
   */
  onPointerDown = (e: PointerEvent) => {
    if (this.state.mode === 'mouse') {
      // Left button only. A right-click is the browser's, and a middle-click
      // is an autoscroll on Windows — neither is a shot.
      if (e.button !== 0) return;
      this.onFire();
      return;
    }
    if (this.dragging) return;
    this.dragging = true;
    this.dragId = e.pointerId;
    this.lastTouch = { x: e.clientX, y: e.clientY };
    this.travel = 0;
    this.downAt = performance.now();
  };

  private travel = 0;
  private downAt = 0;

  onPointerMove = (e: PointerEvent, fit: StageFit) => {
    if (this.state.mode === 'mouse') {
      // The cursor IS the sight — one conversion, no state, no filter. A One
      // Euro filter here would add lag to an input that has no tremor.
      const p = pxToStage(fit, e.clientX, e.clientY);
      this.state.sx = clampAim(p.sx);
      this.state.sy = clampAim(p.sy);
      this.notify();
      return;
    }

    if (!this.dragging || e.pointerId !== this.dragId || !this.lastTouch) return;
    const dx = e.clientX - this.lastTouch.x;
    const dy = e.clientY - this.lastTouch.y;
    this.lastTouch = { x: e.clientX, y: e.clientY };
    this.travel += Math.abs(dx) + Math.abs(dy);
    if (this.state.mode !== 'touch') return;

    // Gain > 1 so the crosshair outruns the thumb and the whole stage is
    // reachable without re-gripping.
    const gain = 1.75 * this.opts.sensitivity;
    this.state.sx = clampAim(this.state.sx + (dx / (fit.w / 2)) * gain);
    this.state.sy = clampAim(this.state.sy + (dy / (fit.h / 2)) * gain);
    this.notify();
  };

  onPointerUp = (e: PointerEvent) => {
    if (this.state.mode === 'mouse') return;
    if (e.pointerId !== this.dragId) return;
    const held = performance.now() - this.downAt;
    this.dragging = false;
    this.dragId = -1;
    this.lastTouch = null;

    /**
     * In gyro mode any tap fires — the phone is the gun and the screen is just
     * a trigger. In touch mode only a tap that did not travel far and did not
     * linger counts, so that aiming and firing are distinguishable with one
     * thumb. 12 px and 320 ms were tuned against the thumb of someone
     * deliberately trying to make it misfire.
     */
    if (this.state.mode === 'gyro') this.onFire();
    else if (this.travel < 12 && held < 320) this.onFire();
    this.travel = 0;
  };

  onPointerCancel = () => {
    this.dragging = false;
    this.dragId = -1;
    this.lastTouch = null;
    this.travel = 0;
  };

  /** The explicit FIRE button, which exists so the trigger is discoverable. */
  fire() {
    this.onFire();
  }

  destroy() {
    this.stopGyro();
    this.onChange = null;
  }

  private notify() {
    this.onChange?.();
  }
}

function clampAim(v: number): number {
  return v < -LIMIT ? -LIMIT : v > LIMIT ? LIMIT : v;
}

function finePointer(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: fine)').matches;
}
